import React, { useState, ChangeEvent, FocusEvent, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Add, InfoOutlined as InfoOutlinedIcon, ArrowBackOutlined } from '@mui/icons-material';
import { Header, HeaderWrapper, Wrapper } from '../SettingsSection/SettingsSection.styles';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useCreateUser, useGetAvailableRolesOfUser, useUpdateUserTeam } from "../../hooks/Users"
import { ERROR_MESSAGES } from '../../constants/commonConstants';
import useShowError from '../../hooks/useShowError';
import { useQueryClient } from '@tanstack/react-query';
import useShowToast from '../../hooks/useShowToast';
import { UserDetailRead } from '../../api/generated/models/UserDetailRead';
import { UserRole } from "../../api/generated"

export const OPCO_REGEX = {
  ALPHABETICSPACE: /^[A-Za-z ]+$/,
  EMAIL: /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/,
};
export const OPCO_VALIDATION_MESSAGES = {
  REQUIRED: 'This field is required',
  ALPHABETICSPACE: 'Only alphabetic characters and spaces allowed',
  MAX_20: 'Maximum 20 characters allowed',
  MAX_50: 'Maximum 50 characters allowed',
  EMAIL_REQUIRED: 'Email is required',
  INVALID_EMAIL: 'Invalid email address',
};

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
}

type UserFormErrors = Partial<Record<keyof UserFormData, string>>;
type UserFormTouched = Partial<Record<keyof UserFormData, boolean>>;

interface Option {
  value: string;
  label: string;
}

const roleKeyToOption: Record<string, Option> = {
  system_admin: { value: 'system_admin', label: 'System Admin' },
  client_admin: { value: 'client_admin', label: 'Project Administrator' },
  team_lead: { value: 'team_lead', label: 'Team Lead' },
  user: { value: 'user', label: 'Reviewer' },
};


const statuses: Option[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const initialFormData: UserFormData = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  status: '',
};
type userFormTypes = {
  mode: 'create' | 'edit';
  existedData: UserDetailRead | null;
  onCancel: () => void;
};
export default function UserCreateForm({ formMode, existedData, onCancel, currentUserRole }) {
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [errors, setErrors] = useState<UserFormErrors>({});
  const [touched, setTouched] = useState<UserFormTouched>({});

  const createUser = useCreateUser().mutate
  const updateUserTeam = useUpdateUserTeam();
  const showError = useShowError()
  const showSuccessToast = useShowToast()
  const {data:availableRolesOfUser} = useGetAvailableRolesOfUser()
  const restrictedRoles = [{ value: 'team_lead', label: 'Team Lead' }, { value: 'user', label: 'Reviewer' },]

const dropdownOptions: Option[] = (availableRolesOfUser?.roles_below || [])
  .filter(roleKey => {
    if (currentUserRole !== 'system_admin' && roleKey === 'system_admin') {
      return false;
    }
    return true;
  })
  .map(roleKey => roleKeyToOption[roleKey])
  .filter(Boolean);

  useEffect(() => {
    if (formMode === 'edit' && existedData) {
      setFormData({
        firstName: existedData.name || '',
        lastName: existedData.last_name || '',
        email: existedData.email || '',
        role: existedData.role || '',
        status: existedData.status === "Active" ? 'Active' : 'Inactive',
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    setTouched({});
  }, [formMode, existedData]);

  // Field-level validation
  const validateField = (name: keyof UserFormData, value: string): string | undefined => {
    switch (name) {
      case 'firstName':
        if (!value) return OPCO_VALIDATION_MESSAGES.REQUIRED;
        if (!OPCO_REGEX.ALPHABETICSPACE.test(value)) return OPCO_VALIDATION_MESSAGES.ALPHABETICSPACE;
        if (value.length > 20) return OPCO_VALIDATION_MESSAGES.MAX_20;
        break;
      case 'lastName':
        if (!value) return OPCO_VALIDATION_MESSAGES.REQUIRED;
        if (!OPCO_REGEX.ALPHABETICSPACE.test(value)) return OPCO_VALIDATION_MESSAGES.ALPHABETICSPACE;
        if (value.length > 20) return OPCO_VALIDATION_MESSAGES.MAX_20;
        break;
      case 'email':
        if (!value) return OPCO_VALIDATION_MESSAGES.EMAIL_REQUIRED;
        if (!OPCO_REGEX.EMAIL.test(value) || value.includes("..")) return OPCO_VALIDATION_MESSAGES.INVALID_EMAIL;
        if (value.trim().length > 50) return OPCO_VALIDATION_MESSAGES.MAX_50;
        break;
      case 'role':
        if (!value) return 'Role is required';
        break;
      default:
        return undefined;
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    const errorMsg = validateField(name as keyof UserFormData, value);
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    const errorMsg = validateField(name as keyof UserFormData, value);
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };


  // On blur, mark as touched and validate
  const handleBlur = (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const errorMsg = validateField(name as keyof UserFormData, value as string);
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  // On save, validate all fields
  const validate = (): boolean => {
    let tempErrors: UserFormErrors = {};
    (Object.keys(formData) as (keyof UserFormData)[]).forEach((key) => {
      const errorMsg = validateField(key, formData[key]);
      if (errorMsg) tempErrors[key] = errorMsg;
    });
    setErrors(tempErrors);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      role: true,
      status: true,
    });
    return Object.keys(tempErrors).length === 0;
  };
  const queryClient = useQueryClient()
  const handleSubmitUserForm = () => {
    const USERS = "users"
    const FILTERED_USERS= "users-filtered"
    if (!validate()) return;
    if (formMode === 'create') {
      const payload = {
        name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        role: formData.role,
      };
      createUser(payload as any, {
        onSuccess: (response) => {
          showSuccessToast("Successfully Created user")
          queryClient.invalidateQueries({ queryKey: [USERS] })
          queryClient.invalidateQueries({ queryKey: [FILTERED_USERS] })
          onCancel()
        },
        onError: (error: any) => {

          if (error?.code === "ERR_NETWORK") {
            showError(ERROR_MESSAGES.NETWORK);
          } else if (error) {
            const status = error?.status
            const detail = error?.body && error?.body?.detail
            if (status === 422 && Array.isArray(detail)) {
              const messages = detail.map((item: any) => {
                return `${item.msg}`
              }).join('\n')
              showError(`${messages}`)
            } else if (status >= 500) {
              showError(ERROR_MESSAGES.SERVER_500)
            } else if (status >= 400 && status <= 500) {
              if (typeof detail === 'string') {
                showError(`${detail}`)
              } else {
                showError(ERROR_MESSAGES.SERVER_400)
              }
            }
            else {
              showError(ERROR_MESSAGES.UPDATE_COMPANY_FAILED)
            }
          } else {
            showError(`${error?.message || ERROR_MESSAGES.UNKNOWN}.`)
          }
        },
      })
    } else if (formMode === 'edit' && existedData) {

      const payload = {
        id: existedData.id,
        name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        role: formData.role,
        is_active: formData.status === "Active",
      };
      updateUserTeam.mutate(
        { id: existedData.id, payload },
        {
          onSuccess: (response: UserDetailRead) => {
            showSuccessToast("Successfully updated user");
            queryClient.invalidateQueries({ queryKey: [USERS] })
            queryClient.invalidateQueries({ queryKey: [FILTERED_USERS] })
            onCancel();
          },
          onError: (error: any) => {
            showError(`Failed to update user: ${error.message}`);
          },
        }
      );
    }
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setErrors({});
    setTouched({});
    onCancel();
  };

  // Only show error if field has been touched and has an error
  const hasError = (field: keyof UserFormData) => Boolean(errors[field] && touched[field]);
  const getErrorMessage = (field: keyof UserFormData) => errors[field];

  return (
    <Wrapper>
      <HeaderWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Header sx={{ display: 'inline' }}>{formMode === "edit" ? "Edit User" : "Create User"} </Header>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackOutlined />}
              onClick={handleCancel}
            >
              Existing users
            </Button>
          </Box>
        </Box>
      </HeaderWrapper>

      {/* User Form */}
      <Box sx={{ mt: 2 }}>
        <Grid container spacing={2} sx={{ marginBottom: '20px', display: 'flex', flexDirection: "column", }}>
          {/* First Name */}
          <Grid item md={3} xs={12} sx={{pr: 1}}>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              error={hasError('firstName')}
              sx={{ height: '56px' }}
            >
              <InputLabel htmlFor="firstName">First Name</InputLabel>
              <OutlinedInput
                id="firstName"
                name="firstName"
                label="First Name"
                value={formData.firstName}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {hasError('firstName') && (
                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                  {getErrorMessage('firstName')}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {/* Last Name */}
          <Grid item md={3} xs={12} sx={{pr: 1}}>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              error={hasError('lastName')}
              sx={{ height: '56px' }}
            >
              <InputLabel htmlFor="lastName">Last Name</InputLabel>
              <OutlinedInput
                id="lastName"
                name="lastName"
                label="Last Name"
                value={formData.lastName}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {hasError('lastName') && (
                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                  {getErrorMessage('lastName')}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {/* Email */}
          <Grid item md={3} xs={12} sx={{pr: 1}}>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              error={hasError('email')}
              sx={{ height: '56px' }}
            >
              <InputLabel htmlFor="email">Email</InputLabel>
              <OutlinedInput
                id="email"
                name="email"
                label="Email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
              />
              {hasError('email') && (
                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                  {getErrorMessage('email')}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {/* Role */}
          <Grid item md={3} xs={12} sx={{ display: 'flex', alignItems: 'center', pr: 1, }}>
            <FormControl
              fullWidth
              size="small"
              variant="outlined"
              error={hasError('role')}
              sx={{ height: '40px', minWidth: '264px' }}
            >
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                id="role"
                name="role"
                value={formData.role}
                label="Role"
                onChange={handleSelectChange}
                onBlur={handleBlur}
              >
                {
                  currentUserRole.toLowerCase() !== UserRole.TEAM_LEAD ? dropdownOptions.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                )): restrictedRoles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>))
                }
              </Select>
              {hasError('role') && (
                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                  {getErrorMessage('role')}
                </Typography>
              )}
            </FormControl>
            <Tooltip title="Select the user's role" arrow>
              <IconButton size="small" sx={{ p: 0.5, ml: 1 }}>
                <InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} />
              </IconButton>
            </Tooltip>
          </Grid>
          {/* Status */}
          {formMode === 'edit' && (
            <Grid item md={5} xs={12} sx={{ display: 'flex', alignItems: 'center', paddingLeft: "0 !important" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.status === 'Active' ? true : false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        status: checked ? 'Active' : 'Inactive',
                      }));
                      setTouched((prev) => ({ ...prev, status: true }));
                      // Optionally validate here if you want
                      // setErrors((prev) => ({ ...prev, status: validateField('status', checked ? 'active' : 'inactive') }));
                    }}
                    name="status"
                    color="primary"
                  />
                }
                label="Active"
                sx={{ ml: 1 }}
              />
              {hasError('status') && (
                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                  {getErrorMessage('status')}
                </Typography>
              )}
            </Grid>
          )}

        </Grid>
      </Box>

      {/* nav buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mt: 1 }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitUserForm}
        >
          {formMode === "create" ? "Create User" : "Update"}
        </Button>
      </Box>
    </Wrapper>
  );
}
